// The signed-in user and the route guard (spec 008). The session lives in an httpOnly cookie that
// the browser sends by itself: nothing about it is read from or written to web storage. What only
// the lazily loaded sign-in and settings pages need is in account.ts, outside the initial chunk.
import { queryOptions } from '@tanstack/react-query'

import { apiFetch, ApiProblem } from './api.js'

export interface Me {
  id: string
  email: string
  name: string
  image: string | null
  role: 'learner' | 'mentor' | 'curator' | 'admin'
  /** The admin viewing Arqam as this user (spec 012); null otherwise. */
  impersonatedBy: { email: string } | null
}

export interface SignInOptions {
  magicLink: boolean
  github: boolean
  google: boolean
  passkey: boolean
}

export const meQuery = queryOptions({
  queryKey: ['me'],
  queryFn: () => apiFetch<Me>('/v1/me'),
  // A 401 is an answer, not a failure worth retrying.
  retry: false,
})

export const signInOptionsQuery = queryOptions({
  queryKey: ['sign-in-options'],
  queryFn: () => apiFetch<SignInOptions>('/v1/sign-in-options'),
  staleTime: 5 * 60_000,
})

/** Pages that need no session. */
export function isPublicPath(pathname: string): boolean {
  return pathname === '/sign-in' || pathname === '/gallery'
}

/**
 * Where to go after signing in: a path inside the app, never another origin ("//evil" or
 * "https://…"), and never back to the sign-in page itself. Tabs and newlines are refused: the
 * URL parser drops them, so "/\t/evil" would become "//evil".
 */
export function safeNext(value: unknown): string {
  if (
    typeof value !== 'string' ||
    !/^\/(?![/\\])/.test(value) ||
    /[\t\n\r]/.test(value) ||
    value.startsWith('/sign-in')
  ) {
    return '/'
  }
  return value
}

/** Is this failure "not signed in" (as opposed to the API being unreachable)? */
export function isSignedOut(error: unknown): boolean {
  return error instanceof ApiProblem && error.status === 401
}
